<script lang="ts">
import {defineComponent, onMounted, ref} from "vue";
import MinimalFooter from "~/layouts/components/MinimalFooter.vue";
import ScrollToTop from "~/components/utils/ScrollToTop.vue";
import CookieConsentContainer from "~/components/utils/CookieConsentContainer.vue";
import MinimalHeader from "~/layouts/components/MinimalHeader.vue";
import {useDocumentStore} from "~/plugins/store/document";
import type {OcrResponse, TranslationList} from "~/types/Utils";
import {translation, useTranslationStore} from '~/plugins/store/translations';
import type {RcaInsuranceCompany} from "~/types/Rca";
import {useRcaStore} from "~/plugins/store/rca";
import {usePageStore} from "~/plugins/store/page";
import type {FaqCategory, FaqQuestion} from "~/types/Page";
import {useNuxtApp} from "#app";

export default defineComponent({
  components: {MinimalHeader, CookieConsentContainer, ScrollToTop, MinimalFooter},
  setup() {
    useHead({
      title: 'Xiga | Direct Booking',
      meta: [
        {
          name: 'description',
          content: 'meta'
        }
      ],
    });

    const documentStore = useDocumentStore();
    const documentList = ref(documentStore.documentList);
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({});
    const rcaStore = useRcaStore();
    const rcaInsuranceCompanies = ref<RcaInsuranceCompany[]>([]);
    const pageStore = usePageStore();
    const faqCategories = ref<FaqCategory[] | null>([]);
    const faqQuestions = ref<FaqQuestion[] | null>([]);
    const faqTab = ref("0");
    const {$toast} = useNuxtApp();

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');

      rcaInsuranceCompanies.value = await rcaStore.getAllRcaInsuranceCompanies();

      const fetchedFaqCategories = await pageStore.getFaqCategories();
      faqCategories.value = fetchedFaqCategories?.filter(c => c.code?.toLowerCase() === 'direct_booking') ?? null;

      faqQuestions.value = await pageStore.getQuestions();
    });

    const handleRedirectToRca = () => {
      navigateTo('/rca?partner_code=directbooking');
    }

    const handleRedirectToTravel = () => {
      navigateTo('/travel?partner_code=directbooking');
    }

    const filteredQuestions = (categoryId: number) => {
      if (faqQuestions.value) {
        return faqQuestions.value.filter(q => q.category_id === categoryId);
      }
      return [];
    };

    return {
      documentList,
      translationList,
      translation,
      handleRedirectToRca,
      handleRedirectToTravel,
      rcaInsuranceCompanies,
      faqCategories,
      faqQuestions,
      faqTab,
      filteredQuestions,
    }
  }
  });
</script>

<template>
  <div style="min-width: 350px;" class="fill-height d-flex flex-column">
    <div class="fill-height">
      <div class="d-flex justify-center py-2 border-1 bg-white">
        <div class="mx-3">
          <a href="/" class="d-flex align-center">
            <img src="/images/logo.webp" alt="Xiga" style="width: 90px; max-width: 100%"/>
          </a>
        </div>
        <div class="mx-3">
          <a href="/" class="d-flex align-center">
            <img src="/images/logo-directbooking.png" alt="Direct Booking" style="width: 250px; max-width: 100%"/>
          </a>
        </div>
      </div>
      <div style="margin-top: -1px;">
        <v-container fluid class="component-container-960mw">
          <v-row class="d-flex justify-center">
            <v-col cols="12" sm="8" md="5" class="mt-5">
              <div class="fw-700 fs-30 text-center text-primary">
                {{ translation(translationList.direct_booking_title, "Vacanță fără griji,") }}<br>
                {{ translation(translationList.direct_booking_subtitle, "cu protecție completă!") }}
              </div>
            </v-col>
            <v-col cols="12" sm="9">
              <v-card
                  class="mx-auto custom-border"
                  rounded="xl"
                  variant="elevated"
                  elevation="15"
              >
                <template v-slot:text>
                  <v-row class="d-flex justify-center">
                    <v-col cols="12" sm="11">
                      <div class="fw-650 text-center fs-18 text-dark my-7">
                        {{ translation(translationList.direct_booking_desc, "Nu lăsa imprevizibilul să îți strice vacanța. Alege acum o asigurare de călătorie și bucură-te de protecție oriunde mergi!") }}<br>
                        {{ translation(translationList.direct_booking_desc_2, "Compară ofertele și contractează rapid cu XIGA, partener DirectBooking.") }}
                      </div>
                    </v-col>
                  </v-row>
                </template>

                <v-card-text class="bg-grey-lighten-4">
                  <v-row class="d-flex justify-center align-center">
                    <v-col cols="12" sm="8" md="8">
                      <v-btn
                        class="block-on-mobile w-100 my-2 no-uppercase fw-600"
                        rounded="lg"
                        prepend-icon="mdi-car"
                        size="x-large"
                        @click="handleRedirectToRca"
                        color="primary">
                        {{ translation(translationList.continue, "Vezi ofertele de RCA") }}
                      </v-btn>
                      <v-btn
                        class="block-on-mobile w-100 my-2 no-uppercase fw-600"
                        rounded="lg"
                        prepend-icon="mdi-airplane"
                        size="x-large"
                        @click="handleRedirectToTravel"
                        color="primary">
                        {{ translation(translationList.continue, "Vezi ofertele de călătorie") }}
                      </v-btn>
                      <div class="text-dark text-center mt-2" style="font-size: 12px">
                        Continuând procesul de scanare, îți dai acordul pentru <a href="/info/terms-and-conditions" target="_blank">prelucrarea
                        datelor cu caracter personal</a> în scopul obținerii de oferte RCA
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </div>

      <!-- RCA Insurance Companies -->
      <v-container fluid class="component-container-full background-default">
        <v-container class="container">
          <div class="d-flex flex-row align-center justify-center my-5">
            <div class="fw-700 fs-30 mx-3 text-center">{{
                translation(translationList.partner_insurance_companies, "Companii de Asigurare Partenere")
              }}
            </div>
          </div>
          <v-row class="d-flex align-center justify-center mt-5">
            <div class="d-flex flex-wrap align-center justify-center pa-1">
              <v-col cols="auto" sm="auto" md="auto" class="pa-1"
                     v-for="(insuranceCompany, index) in rcaInsuranceCompanies"
                     :key="index">
                <v-card height="80" width="130" rounded="lg" flat color="ghost-white"
                        class="d-flex align-center justify-center">
                  <NuxtImg :src="'/images/companies/insurance/thumbnails/' + insuranceCompany.code + '.webp'"
                           :alt="insuranceCompany.name" class="pa-1 w-100 pulse animated"/>
                </v-card>
              </v-col>
            </div>
          </v-row>
        </v-container>
      </v-container>


      <!-- FAQ container -->
      <v-container v-if="faqCategories && faqCategories.length > 0" fluid class="component-container-full">
        <v-container class="container">
          <v-container class="component-container-800mw" style="min-height: 350px;">
            <div class="d-flex flex-column align-center justify-center">
              <div class="fw-700 fs-30 text-center">
                {{ translation(translationList.faq_title, "Întrebări frecvente") }}
              </div>
            </div>
            <div class="mt-10">
              <v-tabs
                  class="faq-tab"
                  v-model="faqTab"
                  fixed-tabs
                  center-active
                  show-arrows
              >
                <v-tab variant="outlined" class="fw-600 text-dark" selected-class="selected" rounded="xl"
                       v-for="(faqCategory, index) in faqCategories" :key="index">
                  {{ faqCategory.category }}
                </v-tab>
              </v-tabs>

              <v-tabs-window v-model="faqTab">
                <v-tabs-window-item
                    v-for="(faqCategory, index) in faqCategories"
                    :key="index"
                    :value="index"
                >
                  <v-expansion-panels class="my-5 border-solid-contrast">
                    <v-expansion-panel
                        bg-color="gray-light"
                        rounded="xl"
                        v-for="(faqQuestion, qIndex) in filteredQuestions(<number>faqCategory.id)"
                        :key="qIndex"
                    >
                      <template v-slot:title>
                        <div class="fw-700 fs-20">{{ faqQuestion.question }}</div>
                      </template>
                      <template v-slot:text>
                        <div class="fw-500 fs-16 mx-4">
                          {{ faqQuestion.answer }}
                        </div>
                      </template>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-tabs-window-item>
              </v-tabs-window>
            </div>
          </v-container>
        </v-container>
      </v-container>
    </div>
    <scroll-to-top></scroll-to-top>
    <cookie-consent-container/>
    <minimal-footer/>
  </div>
</template>

<style scoped>
.custom-border {
  border-top: 10px solid transparent;
  background: linear-gradient(white, white) padding-box,
  linear-gradient(90deg, rgba(var(--v-theme-secondary), 1) 0%, rgba(var(--v-theme-primary), 1) 100%) border-box;
  border-radius: 20px;
}

</style>
